import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { clearCache } from "@/pages/admin/adminCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";
const HTTP_UNAUTHORIZED = 401;
const IS_DEV = process.env.NODE_ENV !== "production";

function devWarn(...args) {
  if (IS_DEV) console.warn(...args);
}

/**
 * useAdminCompetitors — admin auth hook for /admin/market/competitors.
 * Shares the same sessionStorage admin key (`vbb_admin_key`) as the
 * inbox so unlocking once unlocks both within the same tab.
 *
 * When the market section eventually gets its own access key, this is
 * the single place to swap the storage name + verify endpoint.
 */
export default function useAdminCompetitors() {
  const [key, setKey] = useState(
    () => sessionStorage.getItem(KEY_STORAGE) || ""
  );
  const [competitors, setCompetitors] = useState([]);
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const fetchCompetitors = useCallback(async (adminKey) => {
    if (!adminKey) return;
    setStatus({ state: "loading", error: "" });
    try {
      const res = await axios.get(`${API}/admin/market/competitors`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setCompetitors(res.data || []);
      setStatus({ state: "ready", error: "" });
    } catch (err) {
      if (err?.response?.status === HTTP_UNAUTHORIZED) {
        sessionStorage.removeItem(KEY_STORAGE);
        setKey("");
        setStatus({
          state: "unauthorized",
          error: "That key isn't valid. Try again.",
        });
      } else {
        setStatus({ state: "error", error: "Could not load competitors." });
      }
    }
  }, []);

  useEffect(() => {
    if (key) fetchCompetitors(key);
  }, [key, fetchCompetitors]);

  const lock = useCallback(() => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    setKey("");
    setCompetitors([]);
    setStatus({ state: "idle", error: "" });
  }, []);

  const saveNotes = useCallback(
    async (id, notes) => {
      try {
        const res = await axios.patch(
          `${API}/admin/market/competitors/${id}`,
          { notes },
          { headers: { "X-Admin-Key": key } }
        );
        setCompetitors((cs) =>
          cs.map((c) => (c.id === id ? res.data : c))
        );
        return true;
      } catch (err) {
        devWarn("[VBB] saveNotes failed", err);
        return false;
      }
    },
    [key]
  );

  return {
    key,
    competitors,
    status,
    fetchCompetitors,
    lock,
    saveNotes,
  };
}
